import { useCallback, useEffect, useState } from "react";
import type { ShipmentDocument } from "api";
import { shipmentDocumentsApi } from "api";
import { getErrorMessage, isForbidden, isUnauthenticated } from "utils/error.utils";
import type { LoadStatus } from "./useProfile";

type UseShipmentDocuments = {
  documents: ShipmentDocument[];
  status: LoadStatus;
  /** Ask again from scratch, showing the spinner. */
  retry: () => void;
  /**
   * Resolves to null once the file is on the shipment, or to the message to
   * show beside the upload — the back office's own wording for a 422.
   */
  upload: (file: File, containerId: number | null) => Promise<string | null>;
  /** Resolves to null once the document is gone, or to the message to show. */
  remove: (document: ShipmentDocument) => Promise<string | null>;
};

/**
 * One shipment's documents, fetched once for the whole page. The Documents
 * tab and the #shdoc drawer both read this list, so an upload in one shows in
 * the other and the tab badge is simply its length.
 */
export const useShipmentDocuments = (shipmentId: number, onSessionLost: () => void): UseShipmentDocuments => {
  const [documents, setDocuments] = useState<ShipmentDocument[]>([]);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    setStatus("loading");

    shipmentDocumentsApi
      .list(shipmentId)
      .then(({ data }) => {
        if (cancelled) return;
        setDocuments(data.documents);
        setStatus("ready");
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (isUnauthenticated(error)) {
          onSessionLost();
          setStatus("unauthorized");
          return;
        }

        setStatus(isForbidden(error) ? "unverified" : "failed");
      });

    return () => {
      cancelled = true;
    };
  }, [shipmentId, attempt, onSessionLost]);

  const retry = useCallback(() => setAttempt((current) => current + 1), []);

  /** A 401 ends the session; anything else becomes a message for the caller to show. */
  const failure = useCallback(
    (error: unknown, fallback: string): string => {
      if (isUnauthenticated(error)) onSessionLost();

      return getErrorMessage(error, fallback);
    },
    [onSessionLost]
  );

  const upload = useCallback(
    async (file: File, containerId: number | null): Promise<string | null> => {
      try {
        const { data } = await shipmentDocumentsApi.upload(
          shipmentId,
          file,
          containerId === null ? {} : { container_id: containerId }
        );

        // Newest first, as the list comes back from the server.
        setDocuments((current) => [data, ...current]);

        return null;
      } catch (error) {
        return failure(error, "Could not upload this file. Please try again.");
      }
    },
    [shipmentId, failure]
  );

  const remove = useCallback(
    async (document: ShipmentDocument): Promise<string | null> => {
      try {
        await shipmentDocumentsApi.remove(shipmentId, document.id);
        setDocuments((current) => current.filter((item) => item.id !== document.id));

        return null;
      } catch (error) {
        return failure(error, "Could not delete this document. Please try again.");
      }
    },
    [shipmentId, failure]
  );

  return { documents, status, retry, upload, remove };
};
